import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { Menu, X, ChevronDown, ChevronRight, Phone, Search, MessageCircle, CalendarCheck } from 'lucide-react';
import {
  primaryNavItems,
  studyDestinations,
  universities,
  careersJobs,
  studentVisa,
  scholarshipsFinance,
  resources,
  successStories,
  knowledgeCenter,
  aboutLinks,
  destinationsMega,
  NAV_V2,
} from '../data/navigationData';

// P1 nav analytics (GTM-ready, same pattern as NZ cluster events)
const pushDL = (obj) => { window.dataLayer = window.dataLayer || []; window.dataLayer.push(obj); };
const trackPanelClick = (menu) => (e) => {
  const a = e.target.closest('a');
  if (a) pushDL({ event: 'nav_link_click', menu, label: (a.textContent || '').trim(), path: a.getAttribute('href') });
};

const dataMap = {
  studyDestinations,
  universities,
  careersJobs,
  studentVisa,
  scholarshipsFinance,
  resources,
  successStories,
  knowledgeCenter,
  aboutLinks,
};

/* ─── P2 Flagship Destinations Mega (ZNS v1.0) — country cards + contextual Hub & Spoke column ─── */
const DestinationsMegaPanel = ({ onClose }) => {
  const [sel, setSel] = useState(destinationsMega.countries[0].id);
  const country = destinationsMega.countries.find((c) => c.id === sel);
  const select = (id) => {
    if (id !== sel) { setSel(id); pushDL({ event: 'nav_country_select', country: id }); }
  };
  return (
    <div
      className="sa-mega-panel"
      data-testid="mega-panel-studyDestinations"
      onMouseLeave={onClose}
      onClickCapture={trackPanelClick('studyDestinations')}
    >
      <div className="max-w-7xl mx-auto px-6 py-8 grid grid-cols-1 lg:grid-cols-5 gap-8">
        <div className="lg:col-span-3">
          <h4 className="text-xs font-bold tracking-widest text-[#C5A028] mb-4 font-syne uppercase">
            Choose Your Destination
          </h4>
          <div className="grid grid-cols-2 gap-2.5">
            {destinationsMega.countries.map((c) => (
              <Link
                key={c.id}
                to={c.hub}
                onClick={onClose}
                onMouseEnter={() => select(c.id)}
                onFocus={() => select(c.id)}
                data-testid={`nav-country-card-${c.id}`}
                className={`block rounded-xl border px-3.5 py-3 transition-all ${
                  sel === c.id ? 'border-[#C5A028] bg-white/5' : 'border-white/10 hover:border-white/30'
                }`}
              >
                <span className="text-lg mr-2" aria-hidden="true">{c.flag}</span>
                <span className="text-sm font-semibold text-white">Study in {c.name}</span>
                {c.badge && (
                  <span className="inline-block text-[9px] font-bold tracking-wider uppercase px-1.5 py-0.5 rounded ml-1.5 align-middle bg-[#C5A028] text-[#0A192F]">
                    {c.badge}
                  </span>
                )}
                <span className="block text-[11px] text-slate-400 mt-0.5">{c.tagline}</span>
              </Link>
            ))}
          </div>
        </div>
        <div className="lg:col-span-2 lg:border-l lg:border-white/10 lg:pl-8" data-testid="nav-spoke-column">
          <h4 className="text-xs font-bold tracking-widest text-[#C5A028] mb-4 font-syne uppercase">
            <span aria-hidden="true">{country.flag}</span> {country.name} — Hub &amp; Spoke
          </h4>
          <div className="space-y-0.5">
            {country.spokes.map((s) => s.path ? (
              <Link
                key={s.label}
                to={s.path}
                onClick={onClose}
                className="flex items-center gap-2 text-sm text-slate-300 hover:text-white hover:pl-1 transition-all py-1"
              >
                <ChevronRight className="w-3 h-3 shrink-0 text-[#C5A028]" aria-hidden="true" />{s.label}
              </Link>
            ) : (
              <span key={s.label} className="flex items-center gap-2 text-sm text-slate-400 py-1 cursor-default">
                <ChevronRight className="w-3 h-3 shrink-0 text-[#C5A028]" aria-hidden="true" />{s.label}
                <span className="text-[9px] font-bold tracking-wider uppercase px-1.5 py-0.5 rounded bg-slate-600 text-white">Future</span>
              </span>
            ))}
          </div>
          <Link
            to={country.hub}
            onClick={onClose}
            data-testid="nav-explore-hub-link"
            className="inline-flex items-center gap-1 mt-4 text-xs font-semibold text-[#C5A028] hover:text-[#EAB308] transition-colors"
          >
            Explore {country.name} hub &rarr;
          </Link>
        </div>
      </div>
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-3 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs">
          {destinationsMega.tools.map((t) => (
            <Link key={t.path} to={t.path} onClick={onClose} className="text-slate-300 hover:text-white font-medium transition-colors">
              {t.label} &rarr;
            </Link>
          ))}
          <Link
            to="/study-abroad/book-counselling/?source=nav-mega-cta"
            onClick={onClose}
            data-testid="nav-mega-mini-cta"
            className="ml-auto rounded-full px-4 py-1.5 text-[11px] font-bold bg-[#C5A028] hover:bg-[#EAB308] text-[#0A192F] transition-colors whitespace-nowrap"
          >
            Not sure? Get free counselling
          </Link>
        </div>
      </div>
    </div>
  );
};

/* ─── Desktop Mega Menu Panel ─── */
const MegaMenuPanel = ({ dataKey, onClose }) => {
  const data = dataMap[dataKey];
  if (!data) return null;

  // P2 (ZNS v1.0): flagship Destinations experience
  if (dataKey === 'studyDestinations' && NAV_V2) {
    return <DestinationsMegaPanel onClose={onClose} />;
  }

  // Universities — scrollable per-country columns (Germany + UK + Dubai + …)
  if (dataKey === 'universities') {
    return (
      <div
        className="sa-mega-panel"
        data-testid={`mega-panel-${dataKey}`}
        onMouseLeave={onClose}
        onClickCapture={trackPanelClick(dataKey)}
      >
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className={`grid gap-8 ${data.columns.length >= 3 ? 'grid-cols-3' : 'grid-cols-2'}`}>
            {data.columns.map((col, idx) => {
              // Wide-list columns (UK, Dubai, etc.) get 2-col scroll grid; short lists stay 1-col
              const isWide = col.links.length > 10;
              return (
                <div key={col.title}>
                  <h4 className="text-xs font-bold tracking-widest text-[#C5A028] mb-4 font-syne uppercase">
                    {col.title}
                  </h4>
                  <div className={isWide
                    ? 'max-h-[420px] overflow-y-auto pr-2 sa-scrollbar grid grid-cols-2 gap-x-6 gap-y-1'
                    : 'grid grid-cols-1 gap-1'
                  }>
                    {col.links.map((l) => (
                      <Link
                        key={l.path + idx}
                        to={l.path}
                        onClick={onClose}
                        className="text-sm text-slate-300 hover:text-white hover:pl-1 transition-all py-1"
                      >
                        {l.label}
                      </Link>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
          {/* Quick links */}
          {data.quickLinks && (
            <div className="mt-6 pt-4 border-t border-white/10 flex flex-wrap gap-6">
              {data.quickLinks.map((l) => (
                <Link
                  key={l.path}
                  to={l.path}
                  onClick={onClose}
                  className="text-sm font-semibold text-[#C5A028] hover:text-[#EAB308] transition-colors"
                >
                  {l.label} &rarr;
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Careers & Jobs — 9-column → 4-col responsive grid with scroll
  if (dataKey === 'careersJobs') {
    return (
      <div
        className="sa-mega-panel sa-mega-panel--wide"
        data-testid={`mega-panel-${dataKey}`}
        onMouseLeave={onClose}
        onClickCapture={trackPanelClick(dataKey)}
      >
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="flex items-baseline justify-between mb-5">
            <div>
              <h3 className="text-xs uppercase tracking-widest text-[#C5A028] font-syne">Careers &amp; Jobs</h3>
              <p className="text-xs text-slate-400 mt-1">Browse 90+ programmatic guides by destination &amp; field.</p>
            </div>
            <Link
              to="/study-abroad/careers/"
              onClick={onClose}
              className="text-[11px] font-semibold text-[#C5A028] hover:text-[#EAB308]"
            >
              View all guides →
            </Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-6 max-h-[480px] overflow-y-auto pr-2 sa-scrollbar">
            {data.columns.map((col) => (
              <div key={col.title}>
                <h4 className="text-[11px] font-bold tracking-widest text-[#C5A028] mb-2.5 font-syne uppercase">
                  {col.title}
                </h4>
                <div className="space-y-0.5">
                  {col.links.slice(0, 8).map((l) => (
                    <Link
                      key={l.path}
                      to={l.path}
                      onClick={onClose}
                      className="block text-[13px] text-slate-300 hover:text-white hover:pl-1 transition-all py-0.5"
                    >
                      {l.label}
                    </Link>
                  ))}
                  {col.links.length > 8 && (
                    <Link
                      to="/study-abroad/careers/"
                      onClick={onClose}
                      className="block text-[11px] text-[#C5A028] hover:text-[#EAB308] font-semibold mt-1"
                    >
                      + {col.links.length - 8} more →
                    </Link>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Study Destinations / Knowledge Center / Resources - multi-column
  return (
    <div
      className="sa-mega-panel"
      data-testid={`mega-panel-${dataKey}`}
      onMouseLeave={onClose}
      onClickCapture={trackPanelClick(dataKey)}
    >
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className={`grid grid-cols-${data.columns.length} gap-8`} style={{ gridTemplateColumns: `repeat(${data.columns.length}, minmax(0, 1fr))` }}>
          {data.columns.map((col) => (
            <div key={col.title}>
              <h4 className="text-xs font-bold tracking-widest text-[#C5A028] mb-4 font-syne uppercase">
                {col.title}
              </h4>
              {col.links ? (
                <div className="space-y-1">
                  {col.links.map((l) => (
                    <Link
                      key={l.path}
                      to={l.path}
                      onClick={onClose}
                      className="block text-sm text-slate-300 hover:text-white hover:pl-1 transition-all py-1"
                    >
                      {l.label}
                    </Link>
                  ))}
                </div>
              ) : (
                col.sections?.map((sec) => (
                  <div key={sec.subtitle} className="mb-5">
                    <h5 className="text-[11px] font-bold tracking-wider text-slate-400 mb-2 uppercase">
                      {sec.subtitle}
                    </h5>
                    <div className="space-y-1">
                      {sec.links.map((l) => (
                        <Link
                          key={l.path}
                          to={l.path}
                          onClick={onClose}
                          className={`block text-sm py-1 transition-all hover:pl-1 ${
                            sec.isCta
                              ? 'text-[#C5A028] hover:text-[#EAB308] font-medium'
                              : 'text-slate-300 hover:text-white'
                          }`}
                        >
                          {l.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                ))
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

/* ─── Desktop Standard Dropdown ─── */
const DropdownPanel = ({ dataKey, onClose }) => {
  const data = dataMap[dataKey];
  if (!data) return null;

  // careersJobs is an array of sections
  const isSectioned = Array.isArray(data) && data[0]?.subtitle;

  return (
    <div
      className="sa-dropdown-panel"
      data-testid={`dropdown-panel-${dataKey}`}
      onMouseLeave={onClose}
      onClickCapture={trackPanelClick(dataKey)}
    >
      {/* About menu — surface MEA accreditation as an E-E-A-T trust signal */}
      {dataKey === 'aboutLinks' && (
        <div
          className="px-3 pt-3 pb-3 border-b border-white/10"
          data-testid="dropdown-about-mea-credential"
        >
          <div className="text-[9px] font-bold tracking-[0.22em] text-[#C5A028] uppercase mb-1">
            Government of India · MEA Approved
          </div>
          <div className="text-[10px] font-mono text-slate-400 leading-tight">
            RC: B-0762/DEL/PER/1000+/5/9148/2015
          </div>
        </div>
      )}
      {isSectioned ? (
        <div className="py-3 px-2">
          {data.map((sec) => (
            <div key={sec.subtitle} className="mb-3 last:mb-0">
              <h5 className="text-[10px] font-bold tracking-wider text-slate-500 mb-1 px-3 uppercase">
                {sec.subtitle}
              </h5>
              {sec.links.map((l) => (
                <Link
                  key={l.path}
                  to={l.path}
                  onClick={onClose}
                  className="block text-sm text-slate-300 hover:text-white hover:bg-white/5 px-3 py-1.5 rounded transition-all"
                >
                  {l.label}
                </Link>
              ))}
            </div>
          ))}
        </div>
      ) : (
        <div className="py-3 px-2">
          {data.map((l) => (
            <Link
              key={l.path}
              to={l.path}
              onClick={onClose}
              className="block text-sm text-slate-300 hover:text-white hover:bg-white/5 px-3 py-1.5 rounded transition-all"
            >
              {l.label}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

/* ─── P2 Mobile: quick search across destinations + knowledge center ─── */
let SEARCH_INDEX_CACHE = null;
const buildSearchIndex = () => {
  if (SEARCH_INDEX_CACHE) return SEARCH_INDEX_CACHE;
  const idx = [];
  destinationsMega.countries.forEach((c) => {
    idx.push({ label: `Study in ${c.name}`, path: c.hub, group: c.name });
    c.spokes.forEach((s) => { if (s.path && s.path !== c.hub) idx.push({ label: `${c.name} — ${s.label}`, path: s.path, group: c.name }); });
  });
  knowledgeCenter.columns.forEach((col) => col.links.forEach((l) => idx.push({ label: l.label, path: l.path, group: col.title })));
  SEARCH_INDEX_CACHE = idx;
  return idx;
};

const MobileNavSearch = ({ onNavigate }) => {
  const [q, setQ] = useState('');
  const [tracked, setTracked] = useState(false);
  const results = q.trim().length >= 2
    ? buildSearchIndex().filter((r) => r.label.toLowerCase().includes(q.trim().toLowerCase())).slice(0, 8)
    : [];
  return (
    <div className="px-4 pb-3">
      <div className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-full px-3.5 py-2.5 focus-within:border-[#C5A028]">
        <Search className="w-4 h-4 text-slate-400 shrink-0" aria-hidden="true" />
        <input
          type="search"
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            if (!tracked && e.target.value.trim().length >= 2) { pushDL({ event: 'nav_search_used' }); setTracked(true); }
          }}
          placeholder="Search destinations, guides…"
          aria-label="Search destinations and guides"
          data-testid="mobile-nav-search-input"
          className="bg-transparent text-[13px] text-white placeholder-slate-400 outline-none w-full"
        />
      </div>
      {results.length > 0 && (
        <div className="mt-2 rounded-xl border border-white/10 bg-white/5 overflow-hidden" data-testid="mobile-nav-search-results">
          {results.map((r) => (
            <Link
              key={r.path + r.label}
              to={r.path}
              onClick={onNavigate}
              className="block px-4 py-2 text-[13px] text-slate-300 hover:text-white hover:bg-white/5"
            >
              {r.label}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

/* ─── P2 Mobile: Destinations accordion — tap a country, spokes expand inline ─── */
const MobileDestinations = ({ onNavigate }) => {
  const [open, setOpen] = useState(false);
  const [cty, setCty] = useState(null);
  return (
    <div className="border-b border-white/5">
      <button
        onClick={() => {
          if (!open) pushDL({ event: 'mobile_accordion_open', section: 'studyDestinations' });
          setOpen(!open);
        }}
        className="w-full flex items-center justify-between py-3 px-4 text-white font-medium min-h-[48px]"
        aria-expanded={open}
        data-testid="mobile-nav-studyDestinations"
      >
        Study Destinations
        <ChevronDown className={`w-4 h-4 transition-transform ${open ? 'rotate-180' : ''}`} aria-hidden="true" />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <div className="pb-2">
              {destinationsMega.countries.map((c) => (
                <div key={c.id}>
                  <button
                    onClick={() => setCty(cty === c.id ? null : c.id)}
                    className="w-full flex items-center justify-between px-6 py-2.5 text-[13px] text-slate-200 min-h-[48px]"
                    aria-expanded={cty === c.id}
                    data-testid={`mobile-country-${c.id}`}
                  >
                    <span><span aria-hidden="true">{c.flag}</span>&nbsp;&nbsp;Study in {c.name}</span>
                    <ChevronDown className={`w-3.5 h-3.5 transition-transform ${cty === c.id ? 'rotate-180' : ''}`} aria-hidden="true" />
                  </button>
                  <AnimatePresence>
                    {cty === c.id && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className="overflow-hidden"
                      >
                        <div className="pb-2">
                          {c.spokes.map((s) => s.path ? (
                            <Link
                              key={s.label}
                              to={s.path}
                              onClick={onNavigate}
                              className="block text-[12px] text-slate-400 hover:text-white px-10 py-1.5"
                            >
                              {s.label}
                            </Link>
                          ) : (
                            <span key={s.label} className="block text-[12px] text-slate-400 px-10 py-1.5">
                              {s.label} <span className="text-[9px] uppercase tracking-wider text-slate-500">· future</span>
                            </span>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ))}
              <div className="px-6 pt-2">
                {destinationsMega.tools.map((t) => (
                  <Link key={t.path} to={t.path} onClick={onNavigate} className="block text-[12px] text-[#C5A028] font-semibold py-1.5">
                    {t.label} &rarr;
                  </Link>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

/* ─── Mobile Accordion Item ─── */
const MobileAccordion = ({ item, onNavigate }) => {
  const [open, setOpen] = useState(false);
  if (item.type === 'link') {
    return (
      <Link
        to={item.path}
        onClick={onNavigate}
        className="block py-3 px-4 text-white font-medium border-b border-white/5"
      >
        {item.label}
      </Link>
    );
  }

  const data = dataMap[item.dataKey];
  const allLinks = [];

  if (Array.isArray(data)) {
    // flat array or sectioned array
    data.forEach((entry) => {
      if (entry.subtitle) {
        allLinks.push({ type: 'heading', label: entry.subtitle });
        entry.links.forEach((l) => allLinks.push(l));
      } else {
        allLinks.push(entry);
      }
    });
  } else if (data?.columns) {
    data.columns.forEach((col) => {
      allLinks.push({ type: 'heading', label: col.title });
      if (col.links) {
        col.links.forEach((l) => allLinks.push(l));
      }
      if (col.sections) {
        col.sections.forEach((sec) => {
          allLinks.push({ type: 'subheading', label: sec.subtitle });
          sec.links.forEach((l) => allLinks.push(l));
        });
      }
    });
    if (data.quickLinks) {
      allLinks.push({ type: 'heading', label: 'QUICK LINKS' });
      data.quickLinks.forEach((l) => allLinks.push(l));
    }
  }

  return (
    <div className="border-b border-white/5">
      <button
        onClick={() => {
          if (!open) pushDL({ event: 'mobile_accordion_open', section: item.dataKey });
          setOpen(!open);
        }}
        className="w-full flex items-center justify-between py-3 px-4 text-white font-medium"
        data-testid={`mobile-nav-${item.dataKey}`}
      >
        {item.label}
        <ChevronDown className={`w-4 h-4 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <div className="pb-3 max-h-[50vh] overflow-y-auto">
              {allLinks.map((l, i) => {
                if (l.type === 'heading') {
                  return (
                    <div key={`h-${i}-${l.label}`} className="text-[10px] font-bold text-[#C5A028] tracking-wider uppercase px-6 pt-3 pb-1">
                      {l.label}
                    </div>
                  );
                }
                if (l.type === 'subheading') {
                  return (
                    <div key={`sh-${i}-${l.label}`} className="text-[10px] font-bold text-slate-500 tracking-wider uppercase px-8 pt-2 pb-1">
                      {l.label}
                    </div>
                  );
                }
                return (
                  <Link
                    key={l.path + i}
                    to={l.path}
                    onClick={onNavigate}
                    className="block text-sm text-slate-400 hover:text-white px-8 py-1.5"
                  >
                    {l.label}
                  </Link>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

/* ─── Main Navbar ─── */
const StudyAbroadNavbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeMenu, setActiveMenu] = useState(null);
  const navRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
    // setIsScrolled is a stable React setter and handleScroll is declared
    // inside the effect — there are no external dependencies to track.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Close menus on route change
  useEffect(() => {
    setActiveMenu(null);
    setMobileOpen(false);
  }, [location.pathname]);

  const openMenuRef = useRef(null);
  const announceOpen = (key) => {
    if (openMenuRef.current === key) return;
    if (openMenuRef.current) pushDL({ event: 'nav_mega_close', menu: openMenuRef.current });
    openMenuRef.current = key;
    pushDL({ event: 'nav_mega_open', menu: key });
  };
  const closeMenu = () => {
    if (openMenuRef.current) {
      pushDL({ event: 'nav_mega_close', menu: openMenuRef.current });
      openMenuRef.current = null;
    }
    setActiveMenu(null);
  };

  // ESC closes any open panel (WCAG keyboard requirement)
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') {
        if (openMenuRef.current) {
          pushDL({ event: 'nav_mega_close', menu: openMenuRef.current });
          openMenuRef.current = null;
        }
        setActiveMenu(null);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <>
      <nav
        ref={navRef}
        className={`fixed top-0 left-0 right-0 z-[100] transition-all duration-300 ${
          isScrolled
            ? 'bg-[#020617]/95 backdrop-blur-xl shadow-2xl shadow-black/20 border-b border-white/5'
            : 'bg-[#020617]'
        }`}
        data-testid="study-abroad-navbar"
      >
        {/* Top bar */}
        <div className="hidden lg:block bg-[#003087] text-white">
          <div className="max-w-7xl mx-auto px-6 flex items-center justify-between h-8 text-xs">
            <span className="text-slate-300">Part of ZAFCO Global Mobility Group</span>
            <div className="flex items-center gap-4">
              <Link
                to="/"
                className="inline-flex items-center gap-1.5 bg-white/10 hover:bg-white/20 text-slate-100 border border-white/15 hover:border-white/30 rounded-full px-3 py-0.5 text-[10.5px] font-semibold tracking-wide transition-all whitespace-nowrap"
                data-testid="sa-jobs-discovery-pill"
              >
                Looking for Jobs Abroad? <span className="opacity-80">↗</span>
              </Link>
              <a href="tel:+919990533327" className="flex items-center gap-1 hover:text-[#C5A028] transition-colors">
                <Phone className="w-3 h-3" /> +91-9990533327
              </a>
              <Link to="/contact" className="hover:text-[#C5A028] transition-colors">
                Contact Us
              </Link>
            </div>
          </div>
        </div>

        {/* Main nav bar */}
        <div className="max-w-7xl mx-auto px-4 lg:px-6">
          <div className="flex items-center justify-between h-16 lg:h-[68px]">
            {/* Logo */}
            <Link
              to="/study-abroad/"
              className="flex items-center gap-2 shrink-0"
              data-testid="sa-logo-link"
            >
              <img
                src="/zafco-logo.png"
                alt="ZAFCO Study Abroad"
                className="h-10 lg:h-11 w-auto"
              />
              <div className="hidden sm:block border-l border-white/20 pl-3 ml-1">
                <span className="text-white sa-display font-semibold text-[15px] leading-tight block tracking-tight">
                  Study Abroad
                </span>
                <span className="text-[9px] text-[#C5A028] tracking-[0.18em] uppercase font-medium">
                  Powered by ZAFCO Global Mobility
                </span>
              </div>
            </Link>

            {/* Desktop nav items */}
            <div className="hidden xl:flex items-center gap-0.5">
              {primaryNavItems.map((item) => {
                if (item.type === 'link') {
                  return (
                    <Link
                      key={item.label}
                      to={item.path}
                      className={`px-3 py-2 text-[13px] font-medium transition-colors rounded ${
                        location.pathname === item.path
                          ? 'text-[#C5A028]'
                          : 'text-slate-300 hover:text-white'
                      }`}
                    >
                      {item.label}
                    </Link>
                  );
                }
                return (
                  <div
                    key={item.label}
                    className="relative"
                    onMouseEnter={() => {
                      announceOpen(item.dataKey);
                      setActiveMenu(item.dataKey);
                    }}
                  >
                    <button
                      className={`flex items-center gap-1 px-3 py-2 text-[13px] font-medium transition-colors rounded ${
                        activeMenu === item.dataKey
                          ? 'text-[#C5A028]'
                          : 'text-slate-300 hover:text-white'
                      }`}
                      aria-expanded={activeMenu === item.dataKey}
                      aria-haspopup="true"
                      onClick={() => {
                        if (activeMenu === item.dataKey) { closeMenu(); }
                        else { announceOpen(item.dataKey); setActiveMenu(item.dataKey); }
                      }}
                      data-testid={`nav-trigger-${item.dataKey}`}
                    >
                      {item.label}
                      <ChevronDown className={`w-3.5 h-3.5 transition-transform ${activeMenu === item.dataKey ? 'rotate-180' : ''}`} />
                    </button>

                    {/* Dropdown / Mega panel */}
                    <AnimatePresence>
                      {activeMenu === item.dataKey && item.type === 'dropdown' && (
                        <motion.div
                          initial={{ opacity: 0, y: 8 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: 8 }}
                          transition={{ duration: 0.15 }}
                          className="absolute top-full left-0 pt-2 z-50"
                        >
                          <DropdownPanel dataKey={item.dataKey} onClose={closeMenu} />
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              })}
            </div>

            {/* CTA + Mobile toggle */}
            <div className="flex items-center gap-3">
              <Link
                to="/study-abroad/free-profile-evaluation/?source=sa-nav-cta"
                className="hidden md:inline-flex items-center gap-2 bg-[#C5A028] hover:bg-[#EAB308] text-black font-bold text-[11px] tracking-wide leading-tight px-4 py-2.5 rounded-full transition-all hover:shadow-lg hover:shadow-[#C5A028]/30 hover:-translate-y-0.5 whitespace-nowrap"
                data-testid="sa-cta-button"
              >
                Free Profile Evaluation
              </Link>
              <button
                className="xl:hidden p-2 text-white"
                onClick={() => {
                  if (!mobileOpen) pushDL({ event: 'mobile_drawer_open' });
                  setMobileOpen(!mobileOpen);
                }}
                data-testid="sa-mobile-toggle"
              >
                {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* Desktop Mega Menu Panels (full-width, below nav) */}
        <AnimatePresence>
          {activeMenu && (dataMap[activeMenu]?.columns) && (
            <motion.div
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={{ duration: 0.15 }}
              className="absolute left-0 right-0 top-full z-50"
              onMouseLeave={closeMenu}
            >
              <MegaMenuPanel dataKey={activeMenu} onClose={closeMenu} />
            </motion.div>
          )}
        </AnimatePresence>
      </nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, x: '100%' }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: '100%' }}
            transition={{ type: 'tween', duration: 0.25 }}
            className="fixed inset-0 z-[99] bg-[#020617] pt-16 overflow-y-auto xl:hidden"
            data-testid="sa-mobile-menu"
            onClickCapture={trackPanelClick('mobile')}
          >
            <div className="p-4">
              <Link
                to="/study-abroad/free-profile-evaluation/?source=sa-mobile-nav-cta"
                onClick={() => setMobileOpen(false)}
                className="block w-full text-center bg-[#C5A028] hover:bg-[#EAB308] text-black font-bold text-sm py-3 rounded-full mb-4"
              >
                Free Profile Evaluation
              </Link>
            </div>
            {NAV_V2 && <MobileNavSearch onNavigate={() => setMobileOpen(false)} />}
            {primaryNavItems.map((item) => (
              item.dataKey === 'studyDestinations' && NAV_V2 ? (
                <MobileDestinations key={item.label} onNavigate={() => setMobileOpen(false)} />
              ) : (
                <MobileAccordion
                  key={item.label}
                  item={item}
                  onNavigate={() => setMobileOpen(false)}
                />
              )
            ))}
            <div className={`p-4 mt-4 border-t border-white/10 ${NAV_V2 ? 'mb-16' : ''}`}>
              <p className="text-xs text-slate-400 text-center">
                Part of ZAFCO Global Mobility Group
              </p>
            </div>
            {NAV_V2 && (
              <div
                className="fixed bottom-0 left-0 right-0 h-14 bg-[#0A192F] border-t border-white/10 grid grid-cols-3 z-[100]"
                data-testid="mobile-sticky-bar"
              >
                <a
                  href="tel:+919990533327"
                  onClick={() => pushDL({ event: 'mobile_bottombar_click', action: 'call' })}
                  className="flex flex-col items-center justify-center gap-0.5 text-slate-200"
                  data-testid="mobile-bar-call"
                >
                  <Phone className="w-4 h-4" aria-hidden="true" /><span className="text-[9px]">Call</span>
                </a>
                <a
                  href="https://wa.me/919990533327"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => pushDL({ event: 'mobile_bottombar_click', action: 'whatsapp' })}
                  className="flex flex-col items-center justify-center gap-0.5 text-[#25D366]"
                  data-testid="mobile-bar-whatsapp"
                >
                  <MessageCircle className="w-4 h-4" aria-hidden="true" /><span className="text-[9px] text-slate-200">WhatsApp</span>
                </a>
                <Link
                  to="/study-abroad/book-counselling/?source=mobile-sticky-bar"
                  onClick={() => { pushDL({ event: 'mobile_bottombar_click', action: 'book' }); setMobileOpen(false); }}
                  className="flex flex-col items-center justify-center gap-0.5 text-[#C5A028]"
                  data-testid="mobile-bar-book"
                >
                  <CalendarCheck className="w-4 h-4" aria-hidden="true" /><span className="text-[9px]">Book Free</span>
                </Link>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Spacer for fixed nav */}
      <div className="h-16 lg:h-[100px]" />
    </>
  );
};

export default StudyAbroadNavbar;
